import {
  createRouter,
  createWebHistory,
  START_LOCATION,
  RouteRecordRaw,
  RouteLocationNormalized,
  NavigationGuardNext,
} from 'vue-router'
import { i18n } from '@/app/plugins/i18n'
import { usePerformanceTiming } from '@/shared/composables/usePerformanceTiming'
import { PushEventService } from '@/shared/services/PushEventService'
import { VisitorsDirection } from '@/shared/types/other.types'
import { GeneralRoutes, LandingRoutes, PurchaseFlowRoutes } from '@/shared/types/router.types'
import { holderInfoGuard, travelDurationGuard, quoteGuard } from './middleware'

import {
  FlowView,
  FlowHolderInfo,
  FlowTravelDate,
  FlowTravelDuration,
  FlowQuote,
  PaymentInsuranceSuccess,
  FlowPolicyDownload,
} from '@/views/Flow'

const HomeView = () => import('@/views/landing/qic/LandingQIC.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
const NotFound404View = () => import('@/views/NotFound404View.vue')

const { onPerformanceCheckStart } = usePerformanceTiming()

const localeRoute: RouteRecordRaw = {
  path: '/:locale?',
  children: [
    {
      path: 'visitors',
      children: [
        {
          path: '',
          name: 'Home',
          component: HomeView,
        },
        {
          path: '',
          name: VisitorsDirection.PURCHASE,
          component: FlowView,
          meta: {
            name: VisitorsDirection.PURCHASE,
          },
          children: [
            {
              path: 'travel-date',
              name: PurchaseFlowRoutes.TRAVEL_DATE,
              component: FlowTravelDate,
            },
            {
              path: 'holder-info',
              name: PurchaseFlowRoutes.HOLDER_INFO,
              component: FlowHolderInfo,
              beforeEnter: holderInfoGuard,
            },
            {
              path: 'travel-duration',
              name: PurchaseFlowRoutes.TRAVEL_DURATION,
              component: FlowTravelDuration,
              beforeEnter: travelDurationGuard,
            },
            {
              path: 'quote',
              name: PurchaseFlowRoutes.QUOTE,
              component: FlowQuote,
              beforeEnter: quoteGuard,
            },
          ],
        },
        {
          path: 'policy-download',
          name: LandingRoutes.POLICY_DOWNLOAD,
          component: FlowPolicyDownload,
          meta: {
            title: i18n.global.t('qic_visitors.meta_tags.title'),
            description: i18n.global.t('qic_visitors.meta_tags.description'),
            canonical: window.location.href,
          },
        },
        {
          path: 'insurance-result',
          name: PurchaseFlowRoutes.INSURANCE_RESULT,
          component: PaymentInsuranceSuccess,
        },
      ],
    },
    {
      path: ':pathMatch(.*)*',
      name: GeneralRoutes.NOT_FOUND,
      component: NotFoundView,
    },
  ],
}

if (import.meta.env.MODE === 'development') {
  localeRoute.children.push({
    path: '404',
    name: GeneralRoutes.NOT_FOUND_404,
    component: NotFound404View,
  })
}

const BASE_URL: string = '/'

const router = createRouter({
  history: createWebHistory(BASE_URL),
  routes: [localeRoute],
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' }
  },
})

const processUtmParams = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const queryString = Object.keys(to.query)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(to.query[key] as string)}`)
    .join('&')

  const urlParams = new URLSearchParams(queryString)
  const newQuery = { ...to.query }
  let shouldRedirect = false

  const storedKeys = JSON.parse(sessionStorage.getItem('urlParamsKey') || '[]')
  const urlKeys = Object.keys(to.query)
  const allKeys = [...new Set([...storedKeys, ...urlKeys])]
  sessionStorage.setItem('urlParamsKey', JSON.stringify(allKeys))

  allKeys.forEach((key) => {
    const sessionValue = sessionStorage.getItem(key)
    const urlValue = urlParams.get(key)

    if (urlValue) {
      sessionStorage.setItem(key, urlValue)
    } else {
      if (sessionValue && !urlValue) {
        newQuery[key] = sessionValue
        shouldRedirect = true
      } else {
        sessionStorage.removeItem(key)
      }
    }
  })

  if (shouldRedirect && to.fullPath !== from.fullPath) {
    return next({ path: to.path, query: newQuery })
  } else {
    return next()
  }
}

router.beforeEach(async (to, from, next) => {
  onPerformanceCheckStart()

  if (to?.meta.title) {
    // @ts-ignore
    document.title = to.meta.title
    const ogTitle = document.querySelector('head meta[property="og:title"]')
    // @ts-ignore
    ogTitle?.setAttribute('content', to.meta.title)
  }

  if (to?.meta.description) {
    const description = document.querySelector('head meta[name="description"]')
    // @ts-ignore
    description?.setAttribute('content', to.meta.description)
    const ogDescription = document.querySelector('head meta[property="og:description"]')
    // @ts-ignore
    ogDescription?.setAttribute('content', to.meta.description)
  }

  if (to?.meta.canonical) {
    const canonical = document.querySelector('head link[rel="canonical"]')
    // @ts-ignore
    canonical?.setAttribute('href', to.meta.canonical)
    const ogUrl = document.querySelector('head meta[property="og:url"]')
    // @ts-ignore
    ogUrl?.setAttribute('content', to.meta.canonical)
  }

  processUtmParams(to, from, next)
})

router.afterEach((_, from) => {
  window.dispatchEvent(new Event('page-load'))

  const referrer =
    from !== START_LOCATION
      ? `${location.origin}${BASE_URL.slice(0, -1)}${from.fullPath}`
      : document.referrer

  PushEventService.viewPage({ referrer })
})

export default router
